// Shared helpers for the What's New / How-it-works surfaces. The feed itself is now served by the
// BE (api/whatsNew) — this file holds only the presentation glue: relative-time formatting, the
// icon-name → Lucide mapping (BE sends icon strings from a fixed allowlist), and the page-key
// constants that tie a feature page's hero tour to the tours map.

import { Layers, Search, Repeat, Sparkles, Wand2, Image, Video, Palette, Ruler, Box, Users, Zap, Bell, Check, Star, Circle, type LucideIcon } from "lucide-react";

// BE icon allowlist (GET /v1/admin/whats-new/icons) → Lucide components. Unknown names fall back to
// a neutral glyph rather than crashing the highlight row.
const ICONS: Record<string, LucideIcon> = {
  layers: Layers,
  search: Search,
  repeat: Repeat,
  sparkles: Sparkles,
  wand: Wand2,
  image: Image,
  video: Video,
  palette: Palette,
  ruler: Ruler,
  box: Box,
  users: Users,
  zap: Zap,
  bell: Bell,
  check: Check,
  star: Star,
};

// Unknown / missing icon name → a neutral dot (never a decorative glyph that implies meaning).
export const iconFromName = (name?: string | null): LucideIcon => (name && ICONS[name]) || Circle;

// page_key values the FE feature pages look up in the tours map. These MUST match the BE's page_key
// strings (confirmed in the REF-6202 handover) — a mismatch just means the tour never fires (safe).
// Two wiring styles via useFeatureTour:
//   • Pages with a shared PageHeader hero also render the "How it works?" button (PageHeader.secondaryCta):
//     moodboards → TrendsPage · design_canvas → CanvasProjectsPage · tech_packs → TechPackAssetsPage
//     brand_dnas → BrandDnasPage · collection_range_plans → CollectionRangePlansPage
//     photoshoot_studio → StudioProjectsPage · marketplace_studio → MarketplaceStudioPage
//   • Custom-header pages call the hook for its auto-open side effect ONLY (no button — no hero slot):
//     photoshoots → PhotoshootsPage · trend_analysis → TrendAnalysisPage · integrations → IntegrationsPage
//     design_assets / photoshoot_assets / history → MyAssetsPage (route-derived key, single mount point)
export const WHATS_NEW_PAGE_KEYS = {
  moodboards: "moodboards",
  design_canvas: "design_canvas",
  tech_packs: "tech_packs",
  brand_dnas: "brand_dnas",
  collection_range_plans: "collection_range_plans",
  photoshoots: "photoshoots",
  design_assets: "design_assets",
  photoshoot_assets: "photoshoot_assets",
  history: "history",
  integrations: "integrations",
  photoshoot_studio: "photoshoot_studio",
  marketplace_studio: "marketplace_studio",
  trend_analysis: "trend_analysis",
  // How-it-works tours (REF-6263). `discover` covers both Explore-Trends sub-pages (Runway/Social).
  discover: "discover",
  homepage: "homepage",
  account: "account",
} as const;

// Localized "2 months ago" via the platform formatter — no per-string catalog entries needed.
export const formatRelativeTime = (iso: string, locale: string): string => {
  // Accepts either a full ISO timestamp (`published_at`, e.g. "2026-08-20T11:05:00+00:00") — preferred, so
  // fresh items read "3 hours ago" not "N hours since local midnight" — or a bare date (`date`, "YYYY-MM-DD"),
  // which is parsed at local midnight. Detect by the "T" separator; only the date-only form needs it appended.
  const then = new Date(iso?.includes("T") ? iso : `${iso}T00:00:00`).getTime();
  const now = Date.now();

  if (Number.isNaN(then)) return "";

  const diffSeconds = Math.round((then - now) / 1000); // negative → in the past
  const abs = Math.abs(diffSeconds);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];

  const rtf = new Intl.RelativeTimeFormat(locale || "en", { numeric: "auto" });

  for (const [unit, secs] of units) {
    if (abs >= secs) return rtf.format(Math.round(diffSeconds / secs), unit);
  }

  return rtf.format(0, "day");
};
